/**
 * Spaces: the filing layer, against `/api/spaces`.
 *
 * Two shapes differ from the wire and are reconciled here rather than in the
 * surfaces:
 *
 *  - **`code` is optional on the server and required here.** A space row is
 *    identified in ten characters on a meeting row; a null code would render as
 *    a hole. Missing codes fall back to the first three letters of the name.
 *  - **`members` are emails here and `person_id`s there.** The editor picks
 *    people by address, because that is what someone knows about a colleague;
 *    the service keeps a `space_people` join against its own people table. The
 *    translation is below, over `/api/spaces/pickers/people`.
 */
import { apiGet, apiSend } from '@/lib/api'
import type { Space, SpaceCounts, SpaceKind } from './types'

interface WireSpace {
  /** Meetings, series and excerpts filed here. */
  attachment_count?: number
  /** The space's OWN notes — not the notes on its meetings. */
  note_count?: number
  id: string
  kind: SpaceKind
  name: string
  code: string | null
  description: string
  status: Space['status']
  archived_at: string | null
  created_at: string
}

interface WirePerson {
  id: string
  display_name: string | null
  person_type: string
  primary_email: string | null
}

interface WireSpaceDetail {
  space: WireSpace
  people: Array<{ person_id: string; primary_email: string | null }>
}

function toSpace(wire: WireSpace, members: string[]): Space {
  return {
    id: wire.id,
    kind: wire.kind,
    name: wire.name,
    code: wire.code ?? wire.name.slice(0, 3).toUpperCase(),
    description: wire.description ?? '',
    status: wire.status,
    members,
    createdAt: wire.created_at,
    archivedAt: wire.archived_at,
  }
}

/** Active first, then alphabetically — an archived space is a record, not a destination. */
function sorted(spaces: Space[]): Space[] {
  return [...spaces].sort((left, right) => {
    if (left.status !== right.status) return left.status === 'active' ? -1 : 1
    return left.name.localeCompare(right.name)
  })
}

/** The last list the server sent, for the synchronous readers below. */
let CACHE: Space[] = []

/**
 * What each space holds, straight off the list endpoint.
 *
 * It counts what is *filed in* the space — the meetings attached to it and the
 * notes written on it — not the notes belonging to those meetings. Those are a
 * property of the meeting and are counted on its own row; a space that shows
 * "41 notes" because four meetings each have ten is answering a question nobody
 * asked of a space.
 */
const COUNTS = new Map<string, SpaceCounts>()

export function spaceCounts(spaceId: string): SpaceCounts {
  return COUNTS.get(spaceId) ?? { meetings: 0, notes: 0 }
}

/**
 * Synchronous read, for the same reason as `templatesSync`: a meeting row
 * resolves its space id while rendering, and the code it shows is three
 * characters wide. Empty until something has called `listSpaces`.
 */
export function spacesSync(activeOnly = true): Space[] {
  return sorted(activeOnly ? CACHE.filter((space) => space.status === 'active') : CACHE)
}

export function findSpace(id: string | null): Space | undefined {
  if (!id) return undefined
  return CACHE.find((space) => space.id === id)
}

/* ---------- People, by address ---------- */

/**
 * Every person the tenant knows, keyed both ways.
 *
 * Loaded once and reused: the editor resolves a handful of addresses per save,
 * and asking the server to search for each one separately would be a request
 * per member for data that does not change between them.
 */
let people: { byEmail: Map<string, string>; byId: Map<string, string> } | null = null

async function directory(): Promise<NonNullable<typeof people>> {
  if (people) return people
  const { people: rows } = await apiGet<{ people: WirePerson[] }>('/api/spaces/pickers/people')
  const byEmail = new Map<string, string>()
  const byId = new Map<string, string>()
  for (const person of rows) {
    const email = person.primary_email?.toLowerCase()
    if (!email) continue
    byEmail.set(email, person.id)
    byId.set(person.id, email)
  }
  people = { byEmail, byId }
  return people
}

/**
 * Adds and removes so that the space's people end up as `members`.
 *
 * An address the tenant has never seen has no person to attach and is skipped
 * rather than invented — a person record created from a typo would then be
 * offered in every picker afterwards.
 */
async function reconcileMembers(spaceId: string, before: string[], after: string[]): Promise<void> {
  const { byEmail } = await directory()
  const wanted = new Set(after.map((email) => email.toLowerCase()))
  const had = new Set(before.map((email) => email.toLowerCase()))

  const added = [...wanted].filter((email) => !had.has(email))
  const removed = [...had].filter((email) => !wanted.has(email))

  await Promise.all([
    ...added.map((email) => {
      const personId = byEmail.get(email)
      return personId
        ? apiSend('POST', `/api/spaces/${spaceId}/people`, { person_id: personId })
        : Promise.resolve()
    }),
    ...removed.map((email) => {
      const personId = byEmail.get(email)
      return personId
        ? apiSend('DELETE', `/api/spaces/${spaceId}/people/${personId}`)
        : Promise.resolve()
    }),
  ])
}

/** The addresses currently filed against a space. */
async function membersOf(spaceId: string): Promise<string[]> {
  const detail = await apiGet<WireSpaceDetail>(`/api/spaces/${spaceId}`)
  return detail.people
    .map((person) => person.primary_email)
    .filter((email): email is string => Boolean(email))
}

/* ---------- Reads and writes ---------- */

/**
 * The list, with each space's people.
 *
 * `GET /api/spaces` answers with a `people_count` and not the people, so the
 * members shown in the editor cost one detail request per space. That is a
 * request per row, which is only acceptable because spaces are counted in tens
 * — a firm has a handful of live deals, not thousands. The fix, if that ever
 * stops being true, is a field on the list endpoint rather than a change here.
 */
export async function listSpaces(): Promise<Space[]> {
  // `status` defaults to `active` on the server; the page shows archived spaces
  // in their own section, so it asks for both.
  const { spaces } = await apiGet<{ spaces: WireSpace[] }>('/api/spaces?status=all')
  for (const space of spaces) {
    COUNTS.set(space.id, {
      meetings: space.attachment_count ?? 0,
      notes: space.note_count ?? 0,
    })
  }
  const withMembers = await Promise.all(
    spaces.map(async (space) => toSpace(space, await membersOf(space.id).catch(() => [])))
  )
  CACHE = withMembers
  return sorted(CACHE)
}

/**
 * The server's rule, mirrored so the editor rejects what a save would.
 *
 * Six characters, uppercase alphanumerics and dashes. The code appears on every
 * meeting row in a column a few characters wide; a longer one is a code that
 * gets truncated in the one place it exists to be read.
 */
export const SPACE_CODE_MAX_LENGTH = 6

/** Why a code is not acceptable, or null when it is. */
export function spaceCodeError(code: string): string | null {
  const value = code.trim().toUpperCase()
  if (value.length < 2) return 'at least 2 characters'
  if (value.length > SPACE_CODE_MAX_LENGTH) return `at most ${SPACE_CODE_MAX_LENGTH} characters`
  if (!/^[A-Z0-9][A-Z0-9-]*$/.test(value)) return 'letters, numbers and dashes only'
  return null
}

export type SpaceDraft = Pick<Space, 'kind' | 'name' | 'code' | 'description' | 'members'>

function remember(space: Space): Space {
  const index = CACHE.findIndex((item) => item.id === space.id)
  if (index < 0) CACHE = [...CACHE, space]
  else CACHE = CACHE.map((item, at) => (at === index ? space : item))
  return space
}

export async function createSpace(draft: SpaceDraft): Promise<Space> {
  const code = draft.code.trim().toUpperCase()
  const { space } = await apiSend<{ space: WireSpace }>('POST', '/api/spaces', {
    kind: draft.kind,
    name: draft.name,
    code,
    description: draft.description,
  })
  await reconcileMembers(space.id, [], draft.members)
  return remember(toSpace(space, draft.members))
}

export async function updateSpace(id: string, draft: Partial<SpaceDraft>): Promise<Space> {
  const body: Record<string, unknown> = {}
  if (draft.kind !== undefined) body.kind = draft.kind
  if (draft.name !== undefined) body.name = draft.name
  if (draft.code !== undefined) body.code = draft.code.trim().toUpperCase()
  if (draft.description !== undefined) body.description = draft.description

  const { space } = await apiSend<{ space: WireSpace }>('PATCH', `/api/spaces/${id}`, body)

  const before = CACHE.find((item) => item.id === id)?.members ?? []
  const members = draft.members ?? before
  if (draft.members) await reconcileMembers(id, before, draft.members)

  return remember(toSpace(space, members))
}

/**
 * Archive and restore, not delete.
 *
 * Meetings stay filed in an archived space; it simply stops appearing in the
 * pickers. Deleting would have to decide what happens to everything filed in
 * it, and the honest answer — unfile it all — is a destructive operation nobody
 * asked for when they finished a deal. `DELETE /api/spaces/:id` exists and is
 * deliberately not called from here.
 */
export async function setSpaceStatus(id: string, status: Space['status']): Promise<Space> {
  const { space } = await apiSend<{ space: WireSpace }>('PATCH', `/api/spaces/${id}`, { status })
  const members = CACHE.find((item) => item.id === id)?.members ?? []
  return remember(toSpace(space, members))
}

/** Suggest a code from a name: "Project Northwind" → "NWD". */
export function suggestCode(name: string, kind: SpaceKind): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)
    // "Project Northwind" and "Area 51" name the kind, which the kind field
    // already carries — a code of PRO on every project distinguishes nothing.
    .filter((word) => !['project', 'projekt', 'area', 'bereich', kind].includes(word.toLowerCase()))
  if (words.length === 0) return ''
  if (words.length === 1) {
    // One word: drop the vowels after the first letter, which is how people
    // shorten a single name by hand — Northwind → NWD.
    const word = words[0]!
    const consonants = word[0]! + word.slice(1).replace(/[aeiouäöü]/gi, '')
    return consonants.slice(0, 3).toUpperCase()
  }
  return words
    .slice(0, 3)
    .map((word) => word[0]!)
    .join('')
    .toUpperCase()
}
